import { useProfileDraft } from "@/context/ProfileDraftContext";
import type { SectionType } from "@profolio/validators";
import { mapSubscriptionToProductTier } from "@profolio/validators/tiers";
import { displayRegistry } from "@profolio/validators/display-registry";
import {
  VisualChoiceCard,
  type VisualChoiceOption,
} from "@/components/ui/visual-controls/VisualChoiceCard";
import { Label } from "@/components/ui/label";
import { SectionMediaManager } from "./SectionMediaManager";


interface DisplayOptionsEditorProps {
  sectionId: string;
  sectionType: SectionType;
}

export function DisplayOptionsEditor({
  sectionId,
  sectionType,
}: DisplayOptionsEditorProps) {
  const {
    draft,
    effectivePreviewDraft,
    setTransientSectionOverrides,
    setDesignPreviewOverride,
    designPreviewOverride,
    userTier,
    onUpgradeClick,
  } = useProfileDraft();
  const productTier = mapSubscriptionToProductTier(userTier);

  // Check both committed draft and transient layer
  const baseSection = draft?.sections.find((s) => s.id === sectionId);
  const activeSection = effectivePreviewDraft?.sections.find(
    (s) => s.id === sectionId,
  );
  if (!activeSection || !baseSection) return null;

  const registry = displayRegistry[sectionType];
  const currentOptions =
    (activeSection.content as any)?.displayOptions ||
    registry?.defaultOptions ||
    {};

  const handleUpdate = (key: "presentation" | "emphasis", value: string) => {
    const updatedOptions = { ...currentOptions, [key]: value };
    setTransientSectionOverrides((prev) => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        displayOptions: updatedOptions,
      },
    }));
  };

  const handleMediaChange = (key: string, value: any) => {
    const currentProfile = effectivePreviewDraft?.profile || draft?.profile;
    const currentOverrides = currentProfile?.layoutOverrides || {};
    const sectionOverridesById = currentOverrides.sectionOverridesById || {};
    const currentSectionOverride = sectionOverridesById[sectionId] || {};

    const updatedSectionOverride = {
      ...currentSectionOverride,
      [key]: value,
    };

    setDesignPreviewOverride({
      ...(designPreviewOverride || {}),
      layoutOverrides: {
        ...currentOverrides,
        sectionOverridesById: {
          ...sectionOverridesById,
          [sectionId]: updatedSectionOverride,
        },
      },
    });
  };

  const presentationOptions: VisualChoiceOption<string>[] =
    registry?.presentations.map((opt) => ({
      value: opt.id,
      title: opt.label,
      description: opt.description,
      isPro: !opt.allowedFor.includes(productTier),
    })) || [];

  const emphasisOptions: VisualChoiceOption<string>[] =
    registry?.emphases.map((opt) => ({
      value: opt.id,
      title: opt.label,
      description: opt.description,
      isPro: !opt.allowedFor.includes(productTier),
    })) || [];

  const profile = effectivePreviewDraft?.profile || draft?.profile;
  const currentMediaOverrides =
    profile?.layoutOverrides?.sectionOverridesById?.[sectionId] || {};

  const sectionWidthOptions: VisualChoiceOption<string>[] = [
    {
      value: "full-bleed",
      title: "Full Width (100%)",
      description: "Spans the full row with no empty spaces on sides",
    },
    {
      value: "default",
      title: "Half Width (50%)",
      description: "Balanced side-by-side layout block",
    },
    {
      value: "expanded",
      title: "Two-Thirds (66%)",
      description: "Wider highlighted layout card",
    },
    {
      value: "narrow",
      title: "One-Third (33%)",
      description: "Compact card slot",
    },
  ];

  const currentSectionWidth = currentMediaOverrides.sectionWidth || (sectionType === "skills" || sectionType === "about" ? "full-bleed" : "default");

  return (
    <div className="space-y-8 pb-12">
      {/* Section Width & Grid Span Control */}
      <div className="space-y-4">
        <div>
          <Label className="text-base font-semibold">Section Width & Layout</Label>
          <p className="text-sm text-muted-foreground mb-4">
            Control how wide this section appears and eliminate unwanted empty space on sides.
          </p>
        </div>
        <VisualChoiceCard
          value={currentSectionWidth}
          onChange={(val) => handleMediaChange("sectionWidth", val)}
          options={sectionWidthOptions}
        />
      </div>

      <hr className="border-border" />

      {registry && (
        <>
          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">
                Presentation Style
              </Label>
              <p className="text-sm text-muted-foreground mb-4">
                Choose how the content is laid out.
              </p>
            </div>
            <VisualChoiceCard
              value={currentOptions.presentation}
              onChange={(val) => handleUpdate("presentation", val)}
              options={presentationOptions}
              onUpgradeClick={() => onUpgradeClick("presentation_style")}
            />
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">Emphasis</Label>
              <p className="text-sm text-muted-foreground mb-4">
                Adjust the background and border emphasis.
              </p>
            </div>
            <VisualChoiceCard
              value={currentOptions.emphasis}
              onChange={(val) => handleUpdate("emphasis", val)}
              options={emphasisOptions}
              onUpgradeClick={() => onUpgradeClick("emphasis_style")}
            />
          </div>

          <hr className="border-border" />
        </>
      )}

      <div className="space-y-4">
        <div>
          <Label className="text-base font-semibold">Media & Background</Label>
          <p className="text-sm text-muted-foreground mb-4">
            Add background images or a companion media gallery to this section.
          </p>
        </div>
        <SectionMediaManager
          overrides={currentMediaOverrides}
          isPro={productTier !== "starter"}
          onFieldChange={handleMediaChange}
          onUpgradeClick={() => onUpgradeClick("layout_overrides")}
        />
      </div>
    </div>
  );
}
